import { create } from "zustand";
import { hashMarkup } from "./svg-sanitize";

export interface LocalSvgAsset {
  id: string;
  name: string;
  inner: string;
  viewBox: string;
  width: number;
  height: number;
  palette: string[];
  hash: string;
  addedAt: number;
}

const STORAGE_KEY = "floorplan:svg-library:v1";

function loadAssets(): LocalSvgAsset[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr as LocalSvgAsset[];
  } catch {
    return [];
  }
}

function saveAssets(assets: LocalSvgAsset[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assets));
  } catch {}
}

const uid = () => Math.random().toString(36).slice(2, 10);

interface State {
  assets: LocalSvgAsset[];
  addAsset: (a: Omit<LocalSvgAsset, "id" | "addedAt" | "hash"> & { hash?: string }) => LocalSvgAsset;
  renameAsset: (id: string, name: string) => void;
  removeAsset: (id: string) => void;
  importAssets: (assets: LocalSvgAsset[]) => number;
  exportAssets: () => string;
}

export const useSvgLibrary = create<State>((set, get) => ({
  assets: loadAssets(),
  addAsset: (a) => {
    const hash = a.hash ?? hashMarkup(a.inner);
    const existing = get().assets.find((x) => x.hash === hash);
    if (existing) return existing;
    const asset: LocalSvgAsset = {
      id: uid(),
      name: a.name,
      inner: a.inner,
      viewBox: a.viewBox,
      width: a.width,
      height: a.height,
      palette: a.palette,
      hash,
      addedAt: Date.now(),
    };
    const next = [asset, ...get().assets];
    set({ assets: next });
    saveAssets(next);
    return asset;
  },
  renameAsset: (id, name) => {
    const next = get().assets.map((a) => (a.id === id ? { ...a, name } : a));
    set({ assets: next });
    saveAssets(next);
  },
  removeAsset: (id) => {
    const next = get().assets.filter((a) => a.id !== id);
    set({ assets: next });
    saveAssets(next);
  },
  importAssets: (incoming) => {
    const existing = new Map(get().assets.map((a) => [a.hash, a]));
    let added = 0;
    for (const a of incoming) {
      if (!a?.inner || !a?.hash) continue;
      if (existing.has(a.hash)) continue;
      existing.set(a.hash, { ...a, id: uid(), addedAt: a.addedAt ?? Date.now() });
      added++;
    }
    const next = Array.from(existing.values()).sort((a, b) => b.addedAt - a.addedAt);
    set({ assets: next });
    saveAssets(next);
    return added;
  },
  exportAssets: () => JSON.stringify(get().assets, null, 2),
}));
